import {CompensationPayload, SearchCompensation} from 'Quoting/models/DTOs/CompensationPayload';
import {InitDto} from 'Quoting/models/DTOs/Init';
import {
  AvailableQuoteActions,
  CensusClassDTO,
  OpportunityQuote,
  OpportunitySummaryDto,
} from 'Quoting/models/DTOs/Opportunity';
import {PremiumSplitDto} from 'Quoting/models/DTOs/PremiumSplit';
import {A360Lookup, CreateQuoteRequestPayload, PlanWithCoverages} from 'Quoting/models/DTOs/QuoteForm';
import {RatePremiumDetails} from 'Quoting/models/DTOs/RatePremium';
import {MasterPolicyQuote} from 'Quoting/models/DTOs/quote';
import {A360ProductCd, A360ProductModel} from 'Quoting/models/Quoting';
import {getCurrentDateISO} from 'Quoting/utils/dateUtils';
import {getProductCdFromModel} from 'Quoting/utils/helpers';
import {immutableMapToJS, parsePath} from 'Quoting/utils/utilities';
import axios from 'Shared/utilities/axiosInstance';
import {getAxiosConfig, handleResponse} from 'Shared/utilities/fetchHelpers';
import {left, right} from 'fp-ts/lib/Either';
import {Map as ImmutableMap} from 'immutable';
import {DeletePayload} from './apiHelpers';
import {
  getCalculateRatesPayload,
  getMinMaxConfigRequestPayload,
  getPredefinedHierarchicalContractsRequestPayload,
  getRefreshProducerLevelListPayload,
  getRetrieveAttributeValuesPayload,
  getRetrievePlanDefaultValuesPayload,
  getRetrievePlansWithCoverageRequestPayload,
  getUpdatedQuoteObject,
  prepareOfferAPIPayload,
} from './apiRequestHelpers';
import {getAPIUrl} from './config';

import {handleQuoteRatingAPIErrors} from './handleQuoteRatingAPIErrors';
import {
  AttributeBehavior,
  GpluAgeBandsAndSmokingFlags,
  PredefinedHierarchicalContracts,
  RetrievePlanConfigurationRequestPayload,
  RetrievePlansRequestData,
} from './types';

const getAxiosConfigAndHeaders = () => {
  const axiosConfig = getAxiosConfig();

  return {
    ...axiosConfig,
    headers: {
      ...axiosConfig.headers,
    },
  };
};

export default {
  getOpportunitySummary: async (opportunityId: string) => {
    const endpoint = `${opportunityId}/getOpportunityDetails`;
    const response = await axios.get<OpportunitySummaryDto>(getAPIUrl(endpoint), getAxiosConfigAndHeaders());
    return handleResponse(response);
  },

  getOpportunityQuotesList: async (rootId: string, revisionNo: number) => {
    const endpoint = `${rootId}/${revisionNo}/getQuotes`;
    const response = await axios.get<OpportunityQuote[]>(getAPIUrl(endpoint), getAxiosConfigAndHeaders());
    return handleResponse(response);
  },

  getPremiumSplitDetails: async (rootId: string, revisionNo: number) => {
    const endpoint = `premium-splits/${rootId}/${revisionNo}`;
    const response = await axios.get<PremiumSplitDto>(getAPIUrl(endpoint), getAxiosConfigAndHeaders());
    return handleResponse(response);
  },

  getExistingCompensationDetails: async (payload: SearchCompensation) => {
    const endpoint = 'contract-assignments/search';
    const response = await axios.post<CompensationPayload[]>(getAPIUrl(endpoint), payload, getAxiosConfigAndHeaders());
    return handleResponse(response);
  },

  getCompensationInitDetails: async (requestPayload: {}) => {
    const endpoint = 'contract-assignments/init';
    const response = await axios.post<InitDto>(getAPIUrl(endpoint), requestPayload, getAxiosConfigAndHeaders());
    return handleResponse(response);
  },

  validatePayloadDraft: async (requestPayload: CompensationPayload) => {
    const endpoint = `contract-assignments/validate-payload-draft`;
    const response = await axios.post<CompensationPayload>(
      getAPIUrl(endpoint),
      requestPayload,
      getAxiosConfigAndHeaders()
    );
    return handleResponse(response);
  },

  validatePayloadSave: async (requestPayload: CompensationPayload, searchValue: [] | CompensationPayload[]) => {
    const endpoint = 'contract-assignments/validate-and-save';
    const stateVal = searchValue.length > 0 ? 'Valid' : 'Initial';
    const saveCompensationPayload = {
      ...requestPayload,
      state: stateVal,
      contract: {...requestPayload.contract, state: stateVal},
    };
    const response = await axios.post<CompensationPayload>(
      getAPIUrl(endpoint),
      saveCompensationPayload,
      getAxiosConfigAndHeaders()
    );
    return handleResponse(response);
  },

  deleteCompensation: async (requestPayload: DeletePayload) => {
    const endpoint = `contract-assignments/delete`;
    const response = await axios.post<CompensationPayload>(
      getAPIUrl(endpoint),
      requestPayload,
      getAxiosConfigAndHeaders()
    );
    return handleResponse(response);
  },

  getAvailableQuoteActions: async (productCd: A360ProductCd, rootId: string) => {
    const endpoint = `${productCd}/${rootId}/getAvailableQuoteActions`;
    const response = await axios.get<AvailableQuoteActions>(getAPIUrl(endpoint), getAxiosConfigAndHeaders());
    return handleResponse(response);
  },

  retrievePlansWithCoverages: async (productCd: A360ProductModel, requestData: RetrievePlansRequestData) => {
    const endpoint = `${productCd}/retrieve-plans-with-coverage`;
    const requestPayload = getRetrievePlansWithCoverageRequestPayload(requestData);
    const response = await axios.post<PlanWithCoverages[]>(
      getAPIUrl(endpoint),
      requestPayload,
      getAxiosConfigAndHeaders()
    );
    return handleResponse(response);
  },

  getQuoteDetails: async (productModel: A360ProductModel, rootId: string, revisionNo: number) => {
    const endpoint = `${productModel}/quotes/${rootId}/${revisionNo}`;
    const response = await axios.get<MasterPolicyQuote>(getAPIUrl(endpoint), getAxiosConfigAndHeaders());
    return handleResponse(response);
  },

  initiateCreateQuote: async (productModel: A360ProductModel, createQuotePayload: CreateQuoteRequestPayload) => {
    const endpoint = `${productModel}/quotes`;
    const response = await axios.post<{body: {success: MasterPolicyQuote}}>(
      getAPIUrl(endpoint),
      createQuotePayload,
      getAxiosConfigAndHeaders()
    );
    return handleResponse(response);
  },

  loadLookup: async (lookupName: string) => {
    const endpoint = `lookups/${lookupName}/load`;
    const response = await axios.post<{body: {success: A360Lookup[]}}>(
      getAPIUrl(endpoint),
      null,
      getAxiosConfigAndHeaders()
    );
    return handleResponse(response);
  },

  updateQuote: async (
    productCd: A360ProductModel,
    quoteData: ImmutableMap<string, any>,
    updatedCompensation: {
      compensationRateImpactOccurred?: boolean;
      levelEquivalent?: number;
    }
  ) => {
    const endpoint = `${productCd}/quotes`;

    const quote = getUpdatedQuoteObject(immutableMapToJS(quoteData) ?? {});
    if (!quote?.termDetails?.termCd) {
      quote.termDetails.termCd = 'Perpetual';
    }
    quote.levelEquivalent = updatedCompensation.levelEquivalent;

    if (updatedCompensation.compensationRateImpactOccurred) {
      quote.policyDetail.compensationRateImpactOccurred = getCurrentDateISO();
    }

    const response = await axios.put<MasterPolicyQuote>(getAPIUrl(endpoint), quote, getAxiosConfigAndHeaders());
    return handleResponse(response);
  },

  retrieveAttributeValues: async (retrieveAttributeValuesPayload: RetrievePlanConfigurationRequestPayload, coverageCd?: string) => {
    const endpoint = `${retrieveAttributeValuesPayload.productModel}/plan-configuration/retrieve-attributes-value`;
    const requestPayload = getRetrieveAttributeValuesPayload(retrieveAttributeValuesPayload, coverageCd);
    const response = await axios.post(getAPIUrl(endpoint), requestPayload, getAxiosConfigAndHeaders());
    return handleResponse(response);
  },

  retrieveOpenlValues: async (
    requestPayload: RetrievePlanConfigurationRequestPayload,
    behaviorType: AttributeBehavior,
    filterForMaturityAge: boolean = false, // required for GWL
    coverageCd?: string
  ) => {
    if (!requestPayload?.planCd && requestPayload?.productModel !== A360ProductModel.GPLUMaster) {
      return right(undefined);
    }

    const endpoint = `${requestPayload.productModel}/behavior/retrieve-openl-values`;
    const defaultValuesDimensionsPayload = getRetrievePlanDefaultValuesPayload(
      requestPayload.productModel,
      requestPayload.planCd,
      requestPayload.situsStateCd ?? '',
      requestPayload.numberOfEligibleLives ?? 0,
      behaviorType,
      filterForMaturityAge,
      coverageCd
    );

    if (getProductCdFromModel(requestPayload.productModel) == A360ProductCd.CI) {
      defaultValuesDimensionsPayload.attributes = [
        ...defaultValuesDimensionsPayload.attributes,
        {
          _type: 'DataItem',
          id: 'benefitPackagingDetail/plans/0/coverageDefs/0/',
          entityName: 'CICoverageDefinition',
          attributeName: 'planStructure',
          attributeValue: {
            _type: 'Value',
            valueType: 'String',
            stringValue: 'CIWithCancer',
          },
        },
        {
          _type: 'DataItem',
          id: 'benefitPackagingDetail/plans/0/coverageDefs/0/benefitSchedule/coveredConditionsDetails/0',
          entityName: 'CICoveredConditions',
          attributeName: 'coveredConditionsTypeCd',
          attributeValue: {
            _type: 'Value',
            valueType: 'String',
            stringValue: 'InitialDiagnosis',
          },
        },
        {
          _type: 'DataItem',
          id: 'benefitPackagingDetail/plans/0/coverageDefs/0/benefitSchedule/coveredConditionsDetails/1',
          entityName: 'CICoveredConditions',
          attributeName: 'coveredConditionsTypeCd',
          attributeValue: {
            _type: 'Value',
            valueType: 'String',
            stringValue: 'Recurrence',
          },
        },
        {
          _type: 'DataItem',
          id: '/benefitPackagingDetail',
          entityName: 'CIPolicyPackage',
          attributeName: 'packageCd',
          attributeValue: {
            _type: 'Value',
            valueType: 'String',
            stringValue: 'CIPackage',
          },
        },
      ];
    }
    const response = await axios.post<any>(
      getAPIUrl(endpoint),
      defaultValuesDimensionsPayload,
      getAxiosConfigAndHeaders()
    );
    return handleResponse(response);
  },

  fetchCustomerCensusClasses: async (customerRootId: string) => {
    const endpoint = `census-classes?rootId=${customerRootId}`;
    const response = await axios.get<CensusClassDTO[]>(getAPIUrl(endpoint), getAxiosConfigAndHeaders());
    return handleResponse(response);
  },

  calculateRates: async (
    quote: ImmutableMap<string, any>,
    customerCensusClasses: CensusClassDTO[],
    quoteRatesAndPremium: RatePremiumDetails | undefined,
    gpluAgeBandsAndSmokingFlags: GpluAgeBandsAndSmokingFlags
  ) => {
    const productCd = quote.get('_modelName') as string;
    const rootId = quote.getIn(parsePath('_key.rootId')) as string;
    const revisionNo = quote.getIn(parsePath('_key.revisionNo')) as number;

    const endpoint = `${productCd}/quotes/${rootId}/${revisionNo}/update-and-rate`;
    const requestPayload = getCalculateRatesPayload(
      quote,
      customerCensusClasses,
      quoteRatesAndPremium,
      gpluAgeBandsAndSmokingFlags
    );

    const response = await axios.post<any>(getAPIUrl(endpoint), requestPayload, getAxiosConfigAndHeaders());
    if (response?.status === 422) {
      return left(handleQuoteRatingAPIErrors(response.data));
    }
    return handleResponse(response);
  },

  updateEntryPointVariation: async (productCd: string, entryPoint: string, updateData: any) => {
    const endpoint = `${productCd}/offer-management/${entryPoint}`;
    const payload = {
      _key: {
        rootId: updateData._key?.rootId,
        revisionNo: updateData._key?.revisionNo,
        variation: 'quote',
      },
      quote: prepareOfferAPIPayload(immutableMapToJS(updateData)),
    };
    const response = await axios.put<{body: {success: MasterPolicyQuote}}>(
      getAPIUrl(endpoint),
      payload,
      getAxiosConfigAndHeaders()
    );

    return handleResponse(response);
  },

  getQuoteRatesAndPremium: async (productCd: A360ProductModel, rootId: string, revisionNo: number) => {
    const endpoint = `${productCd}Rate/quotes/${rootId}/${revisionNo}/rate-premium-details`;
    const response = await axios.get<RatePremiumDetails>(getAPIUrl(endpoint), getAxiosConfigAndHeaders());

    if (response.status === 404) {
      return right(undefined);
    }
    return handleResponse(response);
  },

  retrieveMinMaxConfigFromOpenL: async (
    requestPayload: RetrievePlanConfigurationRequestPayload,
    currentCoverageCd: string
  ) => {
    const endpoint = `${requestPayload.productModel}/behavior/retrieve-openl-values`;
    const minMaxRequestPayload = getMinMaxConfigRequestPayload(requestPayload, currentCoverageCd);
    const response = await axios.post<any>(getAPIUrl(endpoint), minMaxRequestPayload, getAxiosConfigAndHeaders());
    return handleResponse(response);
  },

  fetchPredefinedHierarchicalContracts: async (quote: ImmutableMap<string, any>) => {
    const requestPayload = getPredefinedHierarchicalContractsRequestPayload(quote);
    const productCode = requestPayload?.premiumSequence?.policyInfo?.productCode;
    const endpoint = `${productCode}/predefined-hierarchical-contracts`;
    const response = await axios.post<PredefinedHierarchicalContracts>(
      getAPIUrl(endpoint),
      requestPayload,
      getAxiosConfigAndHeaders()
    );
    return handleResponse(response);
  },

  refreshProducerLevels: async (productModel: A360ProductModel, quotedCommissionSeries: string) => {
    const endpoint = `${productModel}/plan-configuration/retrieve-attributes-value`;
    const requestPayload = getRefreshProducerLevelListPayload(quotedCommissionSeries);
    const response = await axios.post(getAPIUrl(endpoint), requestPayload, getAxiosConfigAndHeaders());
    return handleResponse(response);
  },

  getGPLUAgeBands: async () => {
    const endpoint = 'age-bands-and-smoker-indicators';
    const response = await axios.get(getAPIUrl(endpoint), getAxiosConfigAndHeaders());
    return handleResponse(response);
  },
};
